import type { Column, ColumnType } from '@features/columns/models'
import type { FilterOperator } from '@features/views/models'

export interface FilterValidationOptions {
  column?: Column
  isOperatorAllowedForAppDataType?: (
    appDataTypeId: string | null | undefined,
    operator: FilterOperator
  ) => boolean
  getFilterOperatorRequiresValue?: (
    operator: FilterOperator,
    appDataTypeId?: string | null
  ) => boolean | null
}

/**
 * A leaf filter is valid when it has a column, uses an operator allowed for
 * the column app datatype, and carries a value when that operator requires
 * one. Empty strings count as missing.
 */
export function isValidLeafFilter(
  filter: {
    column_id: string
    operator: FilterOperator
    value: unknown
  },
  options: FilterValidationOptions = {}
): boolean {
  if (!filter.column_id) return false

  if (filter.operator === 'is_me') {
    return options.column?.supports_is_me_filter === true
  }

  if (
    options.column &&
    options.isOperatorAllowedForAppDataType &&
    !options.isOperatorAllowedForAppDataType(options.column.app_data_type_id, filter.operator)
  ) {
    return false
  }

  const requiresValue =
    options.getFilterOperatorRequiresValue?.(filter.operator, options.column?.app_data_type_id) ??
    true

  if (!requiresValue) return true
  if (filter.value === null || filter.value === undefined) return false
  if (typeof filter.value === 'string' && filter.value.trim() === '') return false
  return true
}

export function coerceValue(value: string, type: ColumnType): unknown {
  switch (type) {
    case 'number':
    case 'currency':
      // Decimal values can exceed JavaScript's safe numeric precision. Keep
      // the user's exact text through the filter model and API serializer;
      // the backend owns datatype-aware numeric parsing and comparison.
      return value
    case 'boolean':
      return value === 'true'
    default:
      return value
  }
}
